/**
 * Granular consent state. AGENTS.md §16: each kind has separate accept
 * states, recording requires fresh in-session double-confirmation, and
 * decisions are mirrored to the server as audit events.
 *
 * The kinds align with the backend ConsentKind enum.
 */
import { create } from "zustand";

export type ConsentKind =
  | "join"
  | "audio"
  | "video"
  | "recording"
  | "snapshot"
  | "geolocation";

export type ConsentDecision = "granted" | "denied" | "revoked";

export type ConsentState = Record<ConsentKind, ConsentDecision | null>;

const initial: ConsentState = {
  join: null,
  audio: null,
  video: null,
  recording: null,
  snapshot: null,
  geolocation: null,
};

type Store = {
  state: ConsentState;
  set: (kind: ConsentKind, decision: ConsentDecision) => void;
  reset: () => void;
};

export const useConsentStore = create<Store>((set) => ({
  state: { ...initial },
  set: (kind, decision) =>
    set((s) => ({ state: { ...s.state, [kind]: decision } })),
  reset: () => set({ state: { ...initial } }),
}));

/** Read-only snapshot of the current consent map. */
export function consentSnapshot(): ConsentState {
  return { ...useConsentStore.getState().state };
}

/** Minimum required for the field-user to proceed into a session. */
export const REQUIRED_FIELD_CONSENTS: ConsentKind[] = ["join", "audio", "video"];

export function fieldUserCanProceed(state: ConsentState): boolean {
  return REQUIRED_FIELD_CONSENTS.every((k) => state[k] === "granted");
}